import type { aiFr } from './ai.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts for lot L08 (governed agents and sovereign AI). Same keys as `ai.fr.ts`. */
export const aiEn: DeepStrings<typeof aiFr> = {
  common: {
    platform: 'Platform',
    disabled: 'Disabled',
    yes: 'Yes',
    no: 'No',
    created: 'Created',
    updated: 'Updated',
    saved: 'Changes saved.',
    tokensUnit: 'tokens',
    modelUsage: 'Model {{model}} · {{total}} tokens',
    tabs: { summary: 'Summary', definition: 'Definition' },
  },
  errors: {
    quota: {
      title: 'Token budget exhausted',
      description: 'The gateway budget of this project is used up for the current window.',
      retryIn_one: 'The budget of this project is used up: try again in {{count}} second.',
      retryIn_other: 'The budget of this project is used up: try again in {{count}} seconds.',
    },
    codes: {
      llm_quota_exceeded: 'The token budget of the project is used up for the current window.',
      AGENT_REVOKED: 'This agent is revoked: every request it makes is refused.',
      APPROVER_IS_AGENT_OWNER: 'You cannot decide: you wrote this agent’s policy or you own its identity.',
      APPROVER_MUST_BE_HUMAN: 'Only a person can decide an approval, never a machine token.',
      APPROVAL_NOT_PENDING: 'This request was already decided, executed, or it expired.',
      REAUTHENTICATION_REQUIRED: 'Sign in again to decide: your sign-in must be less than 5 minutes old.',
      PROVIDER_READ_ONLY: 'The platform provider is read-only.',
      MODEL_READ_ONLY: 'Platform models are read-only.',
      RAG_DOCUMENT_TOO_LARGE: 'The file is larger than 20 MiB.',
      RAG_DOCUMENT_UNSUPPORTED: 'Unsupported format: PDF, text, Markdown, CSV, JSON or HTML.',
      RAG_DOCUMENT_UNREADABLE: 'The file could not be read (a scanned PDF without text?).',
      RAG_DOCUMENT_EMPTY: 'The document holds no text to index.',
      RAG_DIMENSION_MISMATCH: 'The embedding model does not match the dimension of the index.',
      AGENT_PRINCIPAL_NOT_FOUND: 'No identity nor application has this client id in the organization.',
    },
  },
  llm: {
    title: 'LLM gateway',
    subtitle:
      'The organization’s models behind an OpenAI-compatible API: providers, model aliases, token budgets per project and the call log.',
    pii: { badge: 'Personal data masked' },
    tabs: { models: 'Models', budgets: 'Budgets', usages: 'Call log' },
    providers: {
      title: 'Providers',
      subtitle:
        'OpenAI-compatible servers (vLLM, Ollama, llama.cpp, TGI…). The API key is sealed: it is never shown again.',
      new: 'New provider',
      newTitle: 'New provider',
      newSubtitle: 'A model server of the organization, reached by graal-api.',
      created: 'Provider {{name}} created.',
      delete: 'Delete provider',
      confirmDelete: 'Delete {{name}}? Its models are deleted with it.',
      deleted: 'Provider {{name}} deleted.',
      keySealed: 'Sealed key',
      noKey: 'No key',
      platformReadOnly: 'Platform provider: it serves every organization and cannot be changed here.',
      empty: {
        title: 'No provider',
        description: 'Declare an OpenAI-compatible server to route models to it.',
      },
      columns: { name: 'Name', kind: 'Type', baseUrl: 'URL', apiKey: 'API key' },
      fields: {
        name: 'Name',
        kind: 'Server type',
        baseUrl: 'API URL',
        baseUrlHelp: 'The OpenAI-compatible base URL, with /v1.',
        apiKey: 'API key',
        apiKeyHelp: 'Optional. Sealed when saved, never read back.',
        apiKeyKeep: 'Leave empty to keep the current key; type one to replace it.',
        removeApiKey: 'Remove the API key',
        enabled: 'Enabled',
        description: 'Description',
      },
      errors: { baseUrl: 'An http:// or https:// URL.' },
      kinds: {
        openai: 'OpenAI (compatible)',
        ollama: 'Ollama',
        vllm: 'vLLM',
        llamacpp: 'llama.cpp',
        tgi: 'TGI',
      },
      check: {
        title: 'Connection test',
        hint: 'graal-api calls GET /models on this server.',
        run: 'Test the connection',
        reachable_one: 'The server answers: {{count}} model announced.',
        reachable_other: 'The server answers: {{count}} models announced.',
        unreachable: 'The server does not answer.',
      },
    },
    models: {
      title: 'Models',
      subtitle: 'Aliases served by the gateway: the name projects ask for, and the real model that answers.',
      new: 'New model',
      newTitle: 'New model',
      newSubtitle: 'A model alias, routed to a provider.',
      created: 'Model {{name}} created.',
      delete: 'Delete model',
      confirmDelete: 'Delete the alias {{name}}? Calls that name it will be refused.',
      deleted: 'Model {{name}} deleted.',
      searchPlaceholder: 'Search a model…',
      usageHint: 'Projects call this model by its alias “{{name}}” (the model field of the request).',
      empty: { title: 'No model', description: 'Add an alias to serve a model to projects.' },
      columns: {
        name: 'Alias',
        capability: 'Use',
        provider: 'Provider',
        upstreamModel: 'Real model',
        contextWindow: 'Context',
      },
      fields: {
        name: 'Alias',
        nameHelp: 'The name projects ask for (e.g. mistral-small).',
        capability: 'Use',
        provider: 'Provider',
        upstreamModel: 'Real model',
        upstreamModelHelp: 'The model identifier at the provider.',
        contextWindow: 'Context window (tokens)',
        enabled: 'Enabled',
      },
      capabilities: { chat: 'Chat', embedding: 'Embeddings' },
    },
    budgets: {
      title: 'Budgets per project',
      subtitle:
        'Tokens used in the current window (read at {{when}}). Without a written budget, the platform cap applies.',
      searchPlaceholder: 'Search a project…',
      written: 'Written budget',
      platformDefault: 'Platform default',
      meterLabel: 'Consumption of {{project}}',
      detailTitle: 'Budget of {{project}}',
      detailSubtitle: 'Gateway token cap of this project, and its current window.',
      used: 'Used',
      reserved: 'Reserved (calls in flight)',
      remaining: 'Remaining',
      windowStart: 'Window start',
      windowEnd: 'Window end',
      reservedHint:
        'A call reserves its estimate before leaving; the reservation is replaced by the real consumption on the answer.',
      save: 'Save the budget',
      saved: 'Budget of {{project}} saved.',
      reset: 'Back to the default',
      confirmReset: 'Remove the written budget of {{project}}? The platform cap will apply.',
      resetDone: '{{project}} is back to the platform cap.',
      empty: { title: 'No project', description: 'Budgets are written per project.' },
      columns: {
        project: 'Project',
        kind: 'Budget',
        period: 'Window',
        consumption: 'Consumption',
        piiMasking: 'Masking',
      },
      fields: {
        period: 'Window',
        tokenLimit: 'Token cap',
        tokenLimitHelp: 'Beyond it, the gateway answers 429 until the next window.',
        piiMasking: 'Mask personal data',
        piiMaskingHelp:
          'Recognized e-mails, phone numbers, IBANs and identifiers are replaced before reaching the model; the number of masked items is logged.',
      },
      periods: { day: 'Day', month: 'Month' },
    },
    usages: {
      title: 'Call log',
      subtitle:
        'Model, tokens, latency, status and caller of every call — never the content of the exchange.',
      searchPlaceholder: 'Filter by model, operation, caller…',
      allProjects: 'All projects',
      period: 'Period',
      since: { '1d': 'Last 24 h', '7d': 'Last 7 days', '30d': 'Last 30 days' },
      viaKey: 'Project key',
      principal: { user: 'User', client: 'Machine token' },
      split: '{{prompt}} + {{completion}}',
      ms: '{{value}} ms',
      masked_one: '{{count}} masked',
      masked_other: '{{count}} masked',
      empty: { title: 'No call', description: 'No call in this period.' },
      columns: {
        when: 'When',
        model: 'Model',
        project: 'Project',
        caller: 'Caller',
        tokens: 'Tokens',
        latency: 'Latency',
        piiMasked: 'Masked',
        status: 'Status',
      },
    },
  },
  keys: {
    title: 'Gateway API keys',
    subtitle:
      'A key calls the LLM gateway on behalf of the project (its budget and masking). It is shown only when created.',
    new: 'New key',
    newTitle: 'New API key',
    newSubtitle: 'The key is shown only once: copy it before closing.',
    create: 'Create the key',
    revoke: 'Revoke',
    confirmRevoke: 'Revoke the key {{name}}? It will be refused at its next request.',
    revoked: 'Key {{name}} revoked.',
    empty: { title: 'No key', description: 'Create a key to call the gateway from a job or a tool.' },
    status: { active: 'Active', revoked: 'Revoked', expired: 'Expired' },
    columns: {
      name: 'Name',
      hint: 'Key',
      status: 'State',
      createdBy: 'Created by',
      created: 'Created',
      expires: 'Expires',
      lastUsed: 'Last used',
    },
    fields: {
      name: 'Name',
      nameHelp: 'What the key is for (e.g. job-incident-summary).',
      expiresInDays: 'Validity (days)',
      expiresInDaysHelp: 'From 1 to 3,650 days.',
    },
    reveal: {
      title: 'Key {{name}} created',
      copy: 'Copy the key',
      copied: 'Key copied.',
      once: 'This key will never be shown again. Store it in a secrets manager.',
      missing: 'The answer did not hold the key: revoke it and create another one.',
      usage: 'Call the gateway with this key (read from standard input, never written in the command):',
      done: 'I copied the key',
      doneHint: 'Closing this box removes the key from the screen.',
    },
  },
  rag: {
    title: 'Document assistant',
    subtitle:
      'Indexes of the project’s documents, queried in natural language: every answer cites the passages it comes from.',
    badge: 'RAG',
    new: 'New index',
    newTitle: 'New document index',
    newSubtitle: 'The vector dimension is set by the first indexed document.',
    created: 'Index {{name}} created.',
    delete: 'Delete index',
    confirmDelete: 'Delete the index {{name}} with its documents and passages?',
    deleted: 'Index {{name}} deleted.',
    detailSubtitle: 'Document index of the project.',
    searchPlaceholder: 'Search an index…',
    empty: {
      title: 'No index',
      description: 'Create an index, add documents to it, then ask your questions.',
    },
    columns: {
      name: 'Index',
      documents: 'Documents',
      chunks: 'Passages',
      embeddingModel: 'Embedding model',
      backend: 'Storage',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      embeddingModel: 'Embedding model',
      embeddingModelHelp: 'Alias of an “embeddings” model; empty = the platform’s.',
      chunkSize: 'Passage size (characters)',
      chunkSizeHelp: 'From 200 to 8,000.',
      chunkOverlap: 'Overlap (characters)',
      dimension: 'Dimension',
      topK: 'Passages kept',
    },
    tabs: { chat: 'Conversation', search: 'Search', documents: 'Documents' },
    chat: {
      hint: 'Each question goes alone, with the closest passages; tokens are charged to the project.',
      question: 'Your question',
      placeholder: 'E.g. What is the procedure for an incident on a substation?',
      ask: 'Ask',
      thinking: 'Looking for sources and writing…',
      sources: 'Sources',
      cited: 'cited',
      citeLabel: 'See source {{n}}',
    },
    search: {
      query: 'Search',
      placeholder: 'Words, a sentence…',
      run: 'Search',
      none: 'No close passage.',
      score: 'score {{score}}',
      ordinal: 'passage {{n}}',
    },
    documents: {
      title: 'Documents',
      subtitle: 'Each document is split into passages, embedded by the gateway and indexed.',
      searchPlaceholder: 'Search a document…',
      added_one: '{{title}} indexed ({{count}} passage).',
      added_other: '{{title}} indexed ({{count}} passages).',
      remove: 'Remove document',
      removeLabel: 'Remove {{title}}',
      confirmRemove: 'Remove {{title}} from the index?',
      removed: '{{title}} removed.',
      empty: { title: 'No document', description: 'Add a text, drop a file or index a table.' },
      columns: { title: 'Title', type: 'Type', size: 'Size' },
      fields: {
        title: 'Title',
        source: 'Source',
        sourceHelp: 'Optional: a URL or a reference, shown with the citations.',
        content: 'Text',
        query: 'Query',
        queryHelp: 'Read-only, run under YOUR rights; one row per paragraph.',
        datawarehouse: 'Data warehouse',
        datawarehouseHelp: 'Identifier of a data warehouse; empty = the organization’s Trino warehouse.',
        maxRows: 'Rows at most',
      },
      add: {
        submit: 'Index',
        file: 'Drop a file',
        fileInput: 'File to index',
        tooLarge: '{{name}} is larger than 20 MiB.',
        text: { action: 'Add a text', title: 'Add a text', hint: 'Paste a text: it is split and indexed.' },
        table: {
          action: 'Index a table',
          title: 'Index the result of a query',
          hint: 'A read query on a data warehouse of the organization.',
        },
      },
    },
  },
  assist: {
    code: {
      title: 'Code assistant',
      subtitle:
        'Explain, generate or complete code with the organization’s models; charged to the project budget.',
      open: 'Code assistant',
      sheetSubtitle: 'The job’s script is prefilled. Nothing is changed: copy the result if you keep it.',
      run: 'Run the assistant',
      forbidden: 'Your role does not allow using the LLM gateway on this project.',
      fields: {
        mode: 'Action',
        language: 'Language',
        instruction: 'Instruction',
        instructionPlaceholder: 'E.g. Read an S3 CSV and sum the consumption per region.',
        code: 'Code',
        cursor: 'Cursor position',
        cursorHelp: 'In characters from the start; empty = at the end.',
      },
      modes: { explain: 'Explain', generate: 'Generate', complete: 'Complete' },
      languages: {
        python: 'Python',
        sql: 'SQL',
        bash: 'Bash',
        scala: 'Scala',
        r: 'R',
        java: 'Java',
        yaml: 'YAML',
      },
      results: { explain: 'Explanation', code: 'Proposal' },
    },
    pipeline: {
      action: 'Describe the pipeline',
      title: 'Describe the pipeline',
      subtitle:
        'Describe the processing in natural language: the model proposes a low-code DAG, checked by the engine before you see it.',
      noProject: 'Open a project first: tokens are charged to its budget.',
      run: 'Propose a pipeline',
      fields: {
        description: 'Description',
        descriptionPlaceholder:
          'E.g. Read eco2mix as Parquet, keep Île-de-France, average consumption per month, write the result as Parquet.',
        language: 'Target engine',
      },
      languages: { pandas: 'Pandas', pyspark: 'PySpark' },
      status: { valid: 'Checked by the engine', invalid: 'Refused by the engine', unverified: 'Not checked' },
      statusHelp: {
        valid: 'The low-code engine validated this DAG: it can be loaded and run.',
        invalid: 'The engine refused this DAG, even after one repair: read the reason before loading it.',
        unverified: 'The engine did not answer: only the structure of the DAG is checked.',
      },
      attempts_one: '{{count}} attempt · model {{model}}',
      attempts_other: '{{count}} attempts · model {{model}}',
      history: 'Attempt history',
      dag: 'Proposed DAG',
      importRefused: 'This DAG cannot be opened in the canvas: {{detail}}.',
      load: 'Load in the editor',
      loadHint: 'Replaces the canvas; nothing is saved until you click “Save”.',
      loaded: 'Pipeline loaded: review it, then save.',
    },
    describe: {
      action: 'Generate the description',
      title: 'Describe {{table}}',
      subtitle:
        'The model reads the schema and a sample (under your rights) and proposes a description. Nothing is written without your consent.',
      preview: 'Generate the preview',
      proposal: 'Proposal',
      sampleRead_one: '{{count}} sample row read',
      sampleRead_other: '{{count}} sample rows read',
      sampleFailed: 'Unreadable sample ({{detail}}): description from the schema only',
      profile_one: 'Profile of {{count}} column',
      profile_other: 'Profile of {{count}} columns',
      apply: 'Write to the catalog',
      applyHint: 'The table will be marked “generated” until a person validates the description.',
      applied: 'Description of {{table}} written to the catalog, marked “generated”.',
      generatedBadge: 'Generated description',
      fields: {
        project: 'Charged project',
        projectHelp: 'Tokens are taken from its budget.',
        sampleRows: 'Sample rows',
        sampleRowsHelp: 'From 1 to 200.',
      },
    },
  },
  agents: {
    title: 'Agents',
    subtitle:
      'The AI agents connected to the organization, their rights, and the live feed of their calls on this project.',
    badge: 'Governance',
    tabs: { agents: 'Agents', validations: 'Approvals' },
    connected: 'Connected agents',
    connectedSubtitle: 'Governed agents and machine tokens seen in the last 24 hours.',
    ungoverned: 'No policy',
    empty: { title: 'No agent', description: 'No machine token called graal-api in the last 24 hours.' },
    state: { governed: 'Governed', revoked: 'Revoked', ungoverned: 'Not governed' },
    columns: {
      name: 'Agent',
      state: 'State',
      rights: 'Rights',
      lastSeen: 'Last call',
      calls: 'Calls (1 h)',
      denied: 'Refusals (1 h)',
      pending: 'Pending',
    },
    outcomes: {
      allowed: 'Allowed',
      denied: 'Denied',
      approval_required: 'Approval required',
      approved_replay: 'Replayed after approval',
    },
    feed: {
      title: 'Live activity feed',
      subtitle: 'Every call of an agent on this project, and what governance did with it.',
      mode: {
        connecting: 'Connecting…',
        live: 'Live',
        polling: 'Polled every 5 s',
      },
      empty: { title: 'No call', description: 'Agent calls will show up here as they happen.' },
    },
  },
  approvals: {
    title: 'Approvals',
    subtitle: 'Agent actions waiting for a person’s decision.',
    allStatuses: 'All states',
    detailTitle: 'Approval request',
    empty: { title: 'No approval', description: 'No agent action is waiting for a decision.' },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      consumed: 'Executed',
      expired: 'Expired',
    },
    tabs: { request: 'Request' },
    columns: {
      requested: 'Requested',
      agent: 'Agent',
      request: 'Request',
      summary: 'Summary',
      status: 'State',
      expires: 'Expires',
    },
    fields: {
      principal: 'Client id',
      operation: 'Operation',
      request: 'Request',
      decidedBy: 'Decided by',
      decidedAt: 'Decided at',
      consumedAt: 'Executed at',
      body: 'Frozen body {{type}}',
      noBody: 'Request without a body.',
    },
    decision: {
      approve: 'Approve',
      reject: 'Reject',
      approveTitle: 'Approve the agent’s action',
      rejectTitle: 'Reject the agent’s action',
      comment: 'Comment',
      commentHelp: 'Optional; sent to the agent and kept in the log.',
      reauthHint:
        'Deciding needs a sign-in less than 5 minutes old: if needed, the console asks for your credentials again, then replays the decision.',
      rules:
        'The agent will replay its request once, identical (method, path and body); a decision only covers this request.',
      approved: 'Action approved: the agent can replay it.',
      rejected: 'Action rejected.',
      phase: {
        replaying: 'Signed in again: sending the decision…',
        reauthenticating: 'Redirecting to sign-in…',
      },
    },
  },
  policies: {
    title: 'Agent policies',
    subtitle:
      'What each machine token may do: operations, projects, hourly cap, active hours and actions that need approval.',
    new: 'New policy',
    newTitle: 'New agent policy',
    newSubtitle: 'Enforced by graal-api on every request of this token, from the next one.',
    created: 'Policy {{name}} created.',
    delete: 'Delete policy',
    confirmDelete: 'Delete the policy {{name}}? The agent will no longer be governed.',
    deleted: 'Policy {{name}} deleted.',
    searchPlaceholder: 'Search a policy…',
    active: 'Active',
    allOperations: 'All operations',
    allProjects: 'All projects',
    anyTime: 'At any time',
    everyDay: 'every day',
    unlimited: 'No cap',
    perHour: '{{count}}/h',
    approvalsCount_one: '{{count}} to approve',
    approvalsCount_other: '{{count}} to approve',
    hoursShort: '{{from}}:00–{{to}}:00',
    hours: 'From {{from}}:00 to {{to}}:00, {{days}}',
    restore: 'Restore',
    confirmRestore: 'Restore {{name}}? Its requests will be evaluated by the policy again.',
    restored: '{{name}} restored.',
    revokedNotice: 'Revoked by {{by}}: {{reason}}. Every request of this agent is refused.',
    tabs: { activity: 'Recent calls' },
    empty: { title: 'No policy', description: 'Without a policy, a machine token acts with its roles only.' },
    errors: { days: 'ISO days, from 1 (Monday) to 7 (Sunday).' },
    days: {
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
      '7': 'Sunday',
    },
    columns: { name: 'Policy', state: 'State', rights: 'Rights', projects: 'Projects' },
    revoke: {
      action: 'Revoke',
      title: 'Revoke {{name}}',
      description: 'Every request of this agent will be refused from the next one, whatever its token.',
      reason: 'Reason',
      reasonHelp: 'Required; kept with the revocation and shown to administrators.',
      done: '{{name}} revoked.',
    },
    fields: {
      principalId: 'Agent client id',
      principalIdHelp: 'The client id of the identity or the application (Tokens and agents).',
      name: 'Name',
      nameHelp: 'Empty = the identity’s name.',
      description: 'Description',
      allowedOperations: 'Allowed operations',
      allowedOperationsHelp: 'Contract operationId patterns (find*, getJobById…); empty = all.',
      allowedProjects: 'Allowed projects',
      allowedProjectsHelp: 'Project identifiers; empty = all.',
      approvalOperations: 'Need approval',
      approvalOperationsHelp: 'operationId patterns that wait for a person’s decision (delete*…).',
      maxRequestsPerHour: 'Requests per hour cap',
      maxRequestsPerHourHelp: 'Empty = no cap.',
      hoursFrom: 'Active from (hour)',
      hoursTo: 'Until (hour)',
      hoursHelp: 'Hours from 0 to 24, server time zone; empty = at any time.',
      days: 'Active days',
      daysHelp: '1 = Monday … 7 = Sunday; empty = every day.',
      activeHours: 'Active hours',
      createdBy: 'Created by',
    },
  },
  tools: {
    codeTitle: 'Code assistant',
  },
};
